import React from 'react';
import {useCurrencyFormat} from '~/entities/currencies';
import {cn, RsnButton, RsnDialog} from '~/library/ui-kit';
import {useCardSettings} from "../../model/useCardSettings";
import {OperableAccount} from '~/redux/finances/domain';

export interface FeeTopupDialogProps {
    account: OperableAccount;
}

function TopupCardFeeDialog({account}: FeeTopupDialogProps) {
    const cls = {
        info: 'flex justify-between text-[#9da0a6] leading-4',
        divider: 'my-4 w-full border-b border-solid border-[#e9e9e9]',
        subtitle: 'fv-text-l-bold font-bold mb-2',
        text: 'text-[12px] text-neutral-90 leading-4',
    };

    const {onClose} = RsnDialog.useControls();

    const {format} = useCurrencyFormat(account, {parts: ['symbol', 'amount']});

    const cardSettings = useCardSettings(account.currencyCode);

    return (
        <RsnDialog.Window placement='center'>
            <RsnDialog.CenteredHeader text='Card deposit fees' />
            <RsnDialog.Body>
                <div className='mb-2 h-full overscroll-y-auto text-left'>
                    <div className='text-center text-sm'>
                        Processing fee is {cardSettings?.feeRelative}% + {format(cardSettings?.feeAbsolute ?? 0)}
                    </div>
                    <div className='-mb-2 mt-4 rounded-[10px] bg-neutral-20 p-5'>
                        <p className={cls.info}>
                            Max single payment limit
                            <span className={cls.text}>
                                {format(cardSettings?.maximalPerOperationTotal ?? 0)} <br />
                            </span>
                        </p>
                        <p className={cn(cls.info, 'mt-1')}>
                            Max payment per day
                            <span className={cls.text}>
                                {format(cardSettings?.maximalPerDayTotal ?? 0)} <br />
                            </span>
                        </p>
                        <p className={cn(cls.info, 'mt-1')}>
                            Max payment per month
                            <span className={cls.text}>
                                {format(cardSettings?.maximalPerMonthTotal ?? 0)} <br />
                            </span>
                        </p>
                        <p className='mt-1 flex justify-between text-[#9da0a6]'>
                            Min amount
                            <span className={cls.text}>{format(cardSettings?.minimal ?? 0)}</span>
                        </p>
                        <div className={cls.divider} />
                        <h6 className={cls.subtitle}>Important</h6>
                        <p className={cls.text}>
                            Your card must support 3D-Secure, otherwise the transaction will be rejected.
                        </p>
                        <>
                            <div className={cls.divider} />
                            <h6 className={cls.subtitle}>No third-party payments allowed</h6>
                            <p className={cls.text}>The name on your card must match your Raison account name.</p>
                        </>
                        <div className={cls.divider} />
                        <h6 className={cls.subtitle}>False activity</h6>
                        <p className={cls.text}>
                            If no card deposits were made by you or you are a new user registered less than 30 days ago
                            we may ask additional information before depositing funds to your account.
                        </p>
                    </div>
                </div>
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Secondary type='button' onClick={onClose}>
                    I got it
                </RsnButton.Secondary>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );
}

export default TopupCardFeeDialog;
